import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  writeBatch,
  runTransaction,
  increment,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Workspace,
  WorkspaceMember,
  CreateWorkspaceData,
  UpdateWorkspaceData,
  WorkspaceActivity,
  WorkspaceRole,
  WORKSPACE_ROLE_PERMISSIONS
} from '../types/workspace';

// Export the types and permissions for use in other files
export { WORKSPACE_ROLE_PERMISSIONS };
export type { Workspace, WorkspaceMember, CreateWorkspaceData, UpdateWorkspaceData, WorkspaceActivity, WorkspaceRole };
class WorkspaceService {
  // Workspace CRUD operations
  async createWorkspace(userId: string, workspaceData: CreateWorkspaceData): Promise<string> {
    try {
      console.log(`[WorkspaceService] Starting transactional createWorkspace for ${userId}`);

      const { planService } = await import('./planService');
      const { getPlanLimit } = await import('../config/planFeatures');
      const { isAdminEmail } = await import('../config/adminConfig');

      const userRef = doc(db, 'users', userId);
      const workspaceId = doc(collection(db, 'workspaces')).id;
      const workspaceRef = doc(db, 'workspaces', workspaceId);

      await runTransaction(db, async (transaction) => {
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) {
          throw new Error('User profile not found');
        }

        const userData = userSnap.data();
        const planId = await planService.getUserPlan(userId);
        const limit = getPlanLimit(planId, 'maxWorkspaces');

        // Use counter from user doc
        let currentCount = userData.workspaceCount ?? 0;

        // If workspaceCount is missing, we should probably fail safe or use the primed value
        // But since we prime it in PlanService.getUserUsage, and the UI usually calls that first, 
        // it should be there. If not, we'll initialize it to 0 for a new user.

        // Check limits
        if (limit !== Infinity && currentCount >= limit) {
          throw new Error(`Workspace limit reached (${limit}). Please upgrade your plan.`);
        }

        const now = Timestamp.now();

        // 1. Create workspace document
        transaction.set(workspaceRef, {
          ...workspaceData,
          createdAt: now,
          updatedAt: now,
          createdBy: userId,
          isActive: true,
          settings: {
            timezone: workspaceData.timezone,
            currency: workspaceData.currency,
            language: workspaceData.language,
            branding: {
              primaryColor: workspaceData.primaryColor,
              secondaryColor: workspaceData.secondaryColor,
              headingFont: 'Inter',
              headingFontWeight: 'bold' as const,
              headingFontStyle: 'normal' as const,
              bodyFont: 'Inter',
              bodyFontWeight: 'normal' as const,
              bodyFontStyle: 'normal' as const,
              socialLinks: []
            },
            notifications: {
              email: true,
              inApp: true,
              campaignUpdates: true,
              memberActivity: true,
              systemAlerts: true
            },
            integrations: {
              webhooks: []
            }
          }
        });

        // 2. Create workspace membership doc
        const memberId = `${workspaceId}_${userId}`;
        transaction.set(doc(db, 'workspace_members', memberId), {
          workspaceId,
          userId,
          role: 'owner',
          invitedBy: userId,
          joinedAt: now,
          permissions: WORKSPACE_ROLE_PERMISSIONS['owner'],
          isActive: true
        });

        // 3. Increment user workspace count
        transaction.update(userRef, {
          workspaceCount: increment(1),
          updatedAt: now
        });

        console.log(`[WorkspaceService] Transaction: Incremented count and created workspace ${workspaceId}`);
      });

      // Log activity (outside transaction as it's not critical for atomicity)
      await this.logWorkspaceActivity(workspaceId, userId, 'workspace_created', 'Workspace created');

      return workspaceId;
    } catch (error) {
      console.error('Error in transactional createWorkspace:', error);
      throw error;
    }
  }

  async getWorkspace(workspaceId: string): Promise<Workspace | null> {
    try {
      const workspaceRef = doc(db, 'workspaces', workspaceId);
      const workspaceSnap = await getDoc(workspaceRef);

      if (workspaceSnap.exists()) {
        return { id: workspaceSnap.id, ...workspaceSnap.data() } as Workspace;
      }
      return null;
    } catch (error) {
      console.error('Error getting workspace:', error);
      throw error;
    }
  }

  async getUserWorkspaces(userId: string): Promise<Workspace[]> {
    try {
      // Get all workspaces where user is a member
      const membersQuery = query(
        collection(db, 'workspace_members'),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      const membersSnap = await getDocs(membersQuery);
      const workspaceIds = membersSnap.docs.map(doc => doc.data().workspaceId);

      if (workspaceIds.length === 0) {
        return [];
      }

      // Get workspace details
      const workspacesQuery = query(
        collection(db, 'workspaces'),
        where('__name__', 'in', workspaceIds),
        where('isActive', '==', true)
      );
      const workspacesSnap = await getDocs(workspacesQuery);

      return workspacesSnap.docs.map(doc => {
        const data = doc.data() as Workspace;
        return { ...data, id: doc.id };
      });
    } catch (error) {
      console.error('Error getting user workspaces:', error);
      throw error;
    }
  }

  async getWorkspaceStats(workspaceId: string): Promise<{ totalCampaigns: number, totalContacts: number }> {
    try {
      console.log(`Getting stats for workspace: ${workspaceId}`);

      const campaignsQuery = query(collection(db, 'campaigns'), where('workspaceId', '==', workspaceId));
      const campaignsSnap = await getDocs(campaignsQuery);
      console.log(`Found ${campaignsSnap.size} campaigns for workspace ${workspaceId}`);

      const contactsQuery = query(collection(db, 'contacts'), where('workspaceId', '==', workspaceId));
      const contactsSnap = await getDocs(contactsQuery);
      console.log(`Found ${contactsSnap.size} contacts for workspace ${workspaceId}`);

      return {
        totalCampaigns: campaignsSnap.size,
        totalContacts: contactsSnap.size
      };
    } catch (error) {
      console.error(`Error getting stats for workspace ${workspaceId}:`, error);
      return { totalCampaigns: 0, totalContacts: 0 };
    }
  }

  async updateWorkspace(workspaceId: string, updates: UpdateWorkspaceData): Promise<void> {
    try {
      const workspaceRef = doc(db, 'workspaces', workspaceId);
      await updateDoc(workspaceRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating workspace:', error);
      throw error;
    }
  }

  async deleteWorkspace(workspaceId: string): Promise<void> {
    try {
      const batch = writeBatch(db);

      // 1. Mark workspace as inactive
      const workspaceRef = doc(db, 'workspaces', workspaceId);
      const workspaceSnap = await getDoc(workspaceRef);
      const workspaceData = workspaceSnap.data();
      const creatorId = workspaceData?.createdBy;

      batch.update(workspaceRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });

      // Update workspaceCount for the creator
      if (creatorId) {
        const userRef = doc(db, 'users', creatorId);
        batch.update(userRef, {
          workspaceCount: increment(-1),
          updatedAt: serverTimestamp()
        });
      }

      // 2. Move all contacts from this workspace to main contacts (remove workspaceId)
      const contactsQuery = query(
        collection(db, 'contacts'),
        where('workspaceId', '==', workspaceId)
      );

      const contactsSnapshot = await getDocs(contactsQuery);
      console.log(`Moving ${contactsSnapshot.docs.length} contacts from workspace ${workspaceId} to main contacts`);

      contactsSnapshot.docs.forEach(contactDoc => {
        const contactRef = doc(db, 'contacts', contactDoc.id);
        batch.update(contactRef, {
          workspaceId: null, // Remove workspace association
          lastChanged: serverTimestamp()
        });
      });

      // 3. Delete workspace members
      const membersQuery = query(
        collection(db, 'workspace_members'),
        where('workspaceId', '==', workspaceId)
      );

      const membersSnapshot = await getDocs(membersQuery);
      membersSnapshot.docs.forEach(memberDoc => {
        batch.delete(doc(db, 'workspace_members', memberDoc.id));
      });

      // 4. Delete workspace activities
      const activitiesQuery = query(
        collection(db, 'workspace_activities'),
        where('workspaceId', '==', workspaceId)
      );

      const activitiesSnapshot = await getDocs(activitiesQuery);
      activitiesSnapshot.docs.forEach(activityDoc => {
        batch.delete(doc(db, 'workspace_activities', activityDoc.id));
      });

      // Execute all operations in a single batch
      await batch.commit();

      console.log(`Successfully deleted workspace ${workspaceId} and moved ${contactsSnapshot.docs.length} contacts to main contacts`);

    } catch (error) {
      console.error('Error deleting workspace:', error);
      throw error;
    }
  }

  // Workspace member operations
  async addWorkspaceMember(workspaceId: string, userId: string, role: WorkspaceRole): Promise<string> {
    try {
      const now = serverTimestamp();
      // Deterministic id `${workspaceId}_${userId}` — required by Firestore rules
      const memberRef = doc(db, 'workspace_members', `${workspaceId}_${userId}`);
      await setDoc(memberRef, {
        workspaceId,
        userId,
        role,
        invitedBy: userId, // Self-created for owner
        joinedAt: now,
        permissions: WORKSPACE_ROLE_PERMISSIONS[role],
        isActive: true
      });

      // Update workspaceCount if role is owner or admin
      if (role === 'owner' || role === 'admin') {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          workspaceCount: increment(1),
          updatedAt: now
        });
      }

      return memberRef.id;
    } catch (error) {
      console.error('Error adding workspace member:', error);
      throw error;
    }
  }

  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    try {
      const membersQuery = query(
        collection(db, 'workspace_members'),
        where('workspaceId', '==', workspaceId),
        where('isActive', '==', true),
        orderBy('joinedAt', 'desc')
      );
      const membersSnap = await getDocs(membersQuery);

      return membersSnap.docs.map(doc => {
        const data = doc.data() as WorkspaceMember;
        return { ...data, id: doc.id };
      });
    } catch (error) {
      console.error('Error getting workspace members:', error);
      throw error;
    }
  }

  async updateMemberRole(memberId: string, role: WorkspaceRole): Promise<void> {
    try {
      const now = serverTimestamp();
      const memberRef = doc(db, 'workspace_members', memberId);
      const memberSnap = await getDoc(memberRef);
      const memberData = memberSnap.data();
      const userId = memberData?.userId;
      const oldRole = memberData?.role;

      await updateDoc(memberRef, {
        role,
        permissions: WORKSPACE_ROLE_PERMISSIONS[role],
        updatedAt: now
      });

      // Handle workspaceCount if role changed affecting owner/admin status
      if (userId) {
        const wasPrivileged = oldRole === 'owner' || oldRole === 'admin';
        const isPrivileged = role === 'owner' || role === 'admin';

        if (!wasPrivileged && isPrivileged) {
          await updateDoc(doc(db, 'users', userId), { workspaceCount: increment(1), updatedAt: now });
        } else if (wasPrivileged && !isPrivileged) {
          await updateDoc(doc(db, 'users', userId), { workspaceCount: increment(-1), updatedAt: now });
        }
      }
    } catch (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  }

  async removeWorkspaceMember(memberId: string): Promise<void> {
    try {
      const now = serverTimestamp();
      const memberRef = doc(db, 'workspace_members', memberId);
      const memberSnap = await getDoc(memberRef);
      const memberData = memberSnap.data();
      const userId = memberData?.userId;
      const role = memberData?.role;

      await updateDoc(memberRef, {
        isActive: false,
        updatedAt: now
      });

      // Decrement workspaceCount if removed member was owner or admin
      if (userId && (role === 'owner' || role === 'admin')) {
        await updateDoc(doc(db, 'users', userId), {
          workspaceCount: increment(-1),
          updatedAt: now
        });
      }
    } catch (error) {
      console.error('Error removing workspace member:', error);
      throw error;
    }
  }

  async getUserWorkspaceRole(workspaceId: string, userId: string): Promise<WorkspaceRole | null> {
    try {
      const memberQuery = query(
        collection(db, 'workspace_members'),
        where('workspaceId', '==', workspaceId),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      const memberSnap = await getDocs(memberQuery);

      if (memberSnap.empty) {
        return null;
      }

      const memberDoc = memberSnap.docs[0].data() as WorkspaceMember;
      return memberDoc.role as WorkspaceRole;
    } catch (error) {
      console.error('Error getting user workspace role:', error);
      throw error;
    }
  }

  // Activity logging
  async logWorkspaceActivity(
    workspaceId: string,
    userId: string,
    action: string,
    description: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      await addDoc(collection(db, 'workspace_activities'), {
        workspaceId,
        userId,
        action,
        description,
        metadata: metadata || {},
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('Error logging workspace activity:', error);
      // Don't throw error as this is not critical
    }
  }

  async getWorkspaceActivities(workspaceId: string, limitCount: number = 50): Promise<WorkspaceActivity[]> {
    try {
      const activitiesQuery = query(
        collection(db, 'workspace_activities'),
        where('workspaceId', '==', workspaceId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const activitiesSnap = await getDocs(activitiesQuery);

      return activitiesSnap.docs.map(doc => {
        const data = doc.data() as WorkspaceActivity;
        return { ...data, id: doc.id };
      });
    } catch (error) {
      console.error('Error getting workspace activities:', error);
      throw error;
    }
  }

  // Migration helper - create default workspace for existing users
  async createDefaultWorkspaceForUser(userId: string, userEmail: string): Promise<string> {
    try {
      const defaultWorkspaceData: CreateWorkspaceData = {
        name: `${userEmail.split('@')[0]}'s Workspace`,
        description: 'Your default workspace',
        primaryColor: '#3B82F6',
        secondaryColor: '#1E40AF',
        timezone: 'UTC',
        currency: 'USD',
        language: 'en'
      };

      return await this.createWorkspace(userId, defaultWorkspaceData);
    } catch (error) {
      console.error('Error creating default workspace for user:', error);
      throw error;
    }
  }
}

export const workspaceService = new WorkspaceService();
